import ExcelJS from 'exceljs';
import { DayType, PrayerType, RoundingConfig, ScheduleEntry, TimeRule } from '../types';
import { DAY_TYPES, MYZMANIM_FIELDS } from '../config/constants';

export const SCHEDULE_XLSX_HEADERS = [
  'prayer',
  'dayTypes',
  'timeType',
  'baseZman',
  'offsetMinutes',
  'roundTo',
  'roundDirection',
  'fixedTime',
  'note',
  'noteHe',
  'night',
  'selichotFrom',
] as const;

export interface LabelOption<T extends string> {
  value: T;
  en: string;
  he: string;
}

export interface ScheduleXlsxLabelSets {
  prayers: LabelOption<PrayerType>[];
  dayTypes: LabelOption<DayType>[];
  timeTypes: LabelOption<'fixed' | 'zman'>[];
  roundDirections: LabelOption<'up' | 'down' | 'nearest'>[];
  instructionsTitle: { en: string; he: string };
  instructionsBody: { en: string; he: string };
}

export interface CsvParsedRow {
  rowNumber: number;
  data: Omit<ScheduleEntry, 'id' | 'overrides'>;
}

export interface CsvRowError {
  rowNumber: number;
  message: string;
}

export interface CsvParseResult {
  rows: CsvParsedRow[];
  errors: CsvRowError[];
}

function normalize(s: string): string {
  return s.trim().toLowerCase().replace(/[\s'’\-_/]/g, '');
}

function buildLookup(pairs: { value: string; labels: string[] }[]): Record<string, string> {
  const lookup: Record<string, string> = {};
  pairs.forEach(({ value, labels }) => {
    labels.forEach((label) => {
      if (label) lookup[normalize(label)] = value;
    });
  });
  return lookup;
}

const PRAYER_ALIAS_SEED: { value: PrayerType; labels: string[] }[] = [
  { value: 'selichot', labels: ['selichot', 'slichot', 'סליחות'] },
  { value: 'shacharit', labels: ['shacharit', 'shacharis'] },
  { value: 'tekiot', labels: ['tekiot', 'tekios', 'shofar', 'תקיעות', 'תקיעת שופר'] },
  { value: 'megillah', labels: ['megillah', 'megila', 'megilla', 'מגילה', 'קריאת מגילה', 'קריאת המגילה'] },
  { value: 'yizkor', labels: ['yizkor', 'יזכור'] },
  { value: 'musaf', labels: ['musaf', 'mussaf'] },
  { value: 'mincha', labels: ['mincha'] },
  { value: 'kabalatShabbat', labels: ['kabalatShabbat', 'kabbalat shabbat', 'kabbalas shabbos'] },
  { value: 'tefilahZakha', labels: ['tefilahZakha', 'tefila zakha', 'tefillah zaka', 'תפילה זכה'] },
  { value: 'neila', labels: ['neila', 'neilah', 'נעילה'] },
  { value: 'maariv', labels: ['maariv', 'arvit'] },
  { value: 'hakafot', labels: ['hakafot', 'הקפות'] },
  { value: 'custom', labels: ['custom', 'מותאם אישית'] },
];

const DAYTYPE_ALIAS_SEED: { value: DayType; labels: string[] }[] = [
  { value: 'weekday', labels: ['weekday'] },
  { value: 'erevShabbos', labels: ['erevShabbos', 'erev shabbat'] },
  { value: 'shabbos', labels: ['shabbos', 'shabbat'] },
  { value: 'erevYomTov', labels: ['erevYomTov'] },
  { value: 'yomTov', labels: ['yomTov'] },
  { value: 'roshHashana', labels: ['roshHashana', 'rosh hashana', 'ראש השנה'] },
  { value: 'sukkotI', labels: ['sukkotI', 'sukkot i', 'first day of sukkot', 'ראשון של סוכות'] },
  { value: 'simchatTorah', labels: ['simchatTorah', 'simchat torah', 'שמחת תורה'] },
  { value: 'pesachI', labels: ['pesachI', 'pesach i', 'first day of pesach', 'ראשון של פסח'] },
  { value: 'shviiShelPesach', labels: ['shviiShelPesach', 'shvii shel pesach', 'שביעי של פסח'] },
  { value: 'shavuot', labels: ['shavuot', 'שבועות'] },
  { value: 'cholHaMoed', labels: ['cholHaMoed'] },
  { value: 'hoshanaRabba', labels: ['hoshanaRabba', 'hoshana rabba', 'hoshana raba', 'הושענא רבה', 'הושענה רבה'] },
  { value: 'roshChodesh', labels: ['roshChodesh'] },
  { value: 'sheniHamishi', labels: ['sheniHamishi', 'mondays & thursdays', 'sheni chamishi'] },
  { value: 'fastMinor', labels: ['fastMinor', 'fast day', 'taanit'] },
  { value: 'tishaBav', labels: ['tishaBav', 'tisha bav', "tisha b'av", 'תשעה באב'] },
  { value: 'yomKippur', labels: ['yomKippur', 'yom kippur', 'יום כיפור'] },
];

const TIMETYPE_ALIAS_SEED: { value: 'fixed' | 'zman'; labels: string[] }[] = [
  { value: 'fixed', labels: ['fixed'] },
  { value: 'zman', labels: ['zman'] },
];

const ROUNDDIR_ALIAS_SEED: { value: 'up' | 'down' | 'nearest'; labels: string[] }[] = [
  { value: 'up', labels: ['up'] },
  { value: 'down', labels: ['down'] },
  { value: 'nearest', labels: ['nearest'] },
];

function withLabelSets<T extends string>(seed: { value: T; labels: string[] }[], options: LabelOption<T>[]) {
  const seeded = seed.map((s) => {
    const opt = options.find((o) => o.value === s.value);
    return { value: s.value, labels: [...s.labels, ...(opt ? [opt.en, opt.he] : [])] };
  });
  // Any option without its own seed entry is still understood by its labels and key.
  const rest = options
    .filter((o) => !seed.some((s) => s.value === o.value))
    .map((o) => ({ value: o.value, labels: [o.value, o.en, o.he] }));
  return [...seeded, ...rest];
}

const ZMAN_KEY_LOOKUP: Record<string, string> = Object.entries(MYZMANIM_FIELDS).reduce(
  (acc, [key, heLabel]) => {
    acc[normalize(key)] = key;
    acc[normalize(heLabel)] = key;
    return acc;
  },
  {} as Record<string, string>
);
// Candle lighting (Phase 6): not a MyZmanim field, worked out by the app.
['candleLighting', 'candle lighting', 'הדלקת נרות', 'זמן הדלקת נרות'].forEach((l) => {
  ZMAN_KEY_LOOKUP[normalize(l)] = 'candleLighting';
});

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Stable order for dayTypes in match keys and saved entries. */
export function orderedDayTypes(dayTypes?: DayType[] | null): DayType[] {
  if (!Array.isArray(dayTypes) || dayTypes.length === 0) return [];
  const known = DAY_TYPES.filter((d) => dayTypes.includes(d));
  const extras = dayTypes.filter((d) => !DAY_TYPES.includes(d));
  return [...known, ...extras];
}

/**
 * Upsert key: prayer + sorted dayTypes + English note (blank/undefined treated equal).
 * Changing the note creates a new entry rather than updating an existing one.
 */
export function scheduleMatchKey(entry: {
  prayerType: PrayerType;
  dayTypes?: DayType[] | null;
  note?: string;
  night?: 'start' | 'end';
}): string {
  const days = orderedDayTypes(entry.dayTypes).join(',');
  const noteKey = (entry.note || '').trim().toLowerCase();
  // Friday-night Maariv and the Maariv after Shabbos are different entries.
  const night = entry.prayerType === 'maariv' ? `|${entry.night ?? 'end'}`
    : (entry.prayerType === 'hakafot' || entry.prayerType === 'megillah') && entry.night === 'start' ? '|start' : '';
  return `${entry.prayerType}|${days}|${noteKey}${night}`;
}

const NIGHT_LOOKUP: Record<string, 'start' | 'end'> = {
  start: 'start', night: 'start', 'תחילה': 'start', 'תחילתהיום': 'start', 'לילהשלפני': 'start', 'לפני': 'start',
  end: 'end', after: 'end', 'סוף': 'end', 'סוףהיום': 'end', 'לילהשאחרי': 'end', 'אחרי': 'end',
};

const SELICHOT_FROM_LOOKUP: Record<string, 'elul2' | 'firstNight'> = {
  elul2: 'elul2', '2elul': 'elul2', 'ב׳אלול': 'elul2', 'באלול': 'elul2', sefardi: 'elul2', 'ספרד': 'elul2',
  firstnight: 'firstNight', ashkenaz: 'firstNight', 'לילהראשון': 'firstNight', 'אשכנז': 'firstNight',
};

function cellText(row: ExcelJS.Row, col: number): string {
  const v = row.getCell(col).value;
  if (v === null || v === undefined) return '';
  if (typeof v === 'object' && v !== null) {
    if ('text' in (v as any)) return String((v as any).text).trim();
    if ('result' in (v as any)) return String((v as any).result).trim();
  }
  return String(v).trim();
}

function parseDayTypesCell(
  raw: string,
  dayTypeLookup: Record<string, string>
): { dayTypes?: DayType[]; error?: string } {
  if (!raw.trim()) {
    return { error: 'Missing dayTypes — enter one or more day types separated by commas' };
  }
  const tokens = raw.split(',').map((t) => t.trim()).filter(Boolean);
  if (tokens.length === 0) {
    return { error: 'Missing dayTypes — enter one or more day types separated by commas' };
  }

  const resolved: DayType[] = [];
  for (const token of tokens) {
    const dt = dayTypeLookup[normalize(token)] as DayType | undefined;
    if (!dt) {
      return {
        error: `Unknown day type "${token}" — use labels like Weekday or ימות החול, comma-separated`,
      };
    }
    if (!resolved.includes(dt)) resolved.push(dt);
  }
  return { dayTypes: orderedDayTypes(resolved) };
}

function prayerDayTypesAllowed(prayerType: PrayerType, dayTypes: DayType[]): string | null {
  if (prayerType === 'tefilahZakha') {
    if (!dayTypes.includes('yomKippur')) {
      return 'Tefilah Zakha belongs to Yom Kippur — put Yom Kippur in dayTypes';
    }
  }
  if (dayTypes.includes('hoshanaRabba') && prayerType !== 'shacharit') {
    return 'Hoshana Rabba is only for Shacharis';
  }
  if (prayerType === 'neila') {
    if (!dayTypes.includes('yomKippur')) {
      return 'Neila is only allowed when dayTypes includes Yom Kippur';
    }
  }
  return null;
}

export function parseScheduleXlsx(
  buffer: ArrayBuffer,
  labelSets: ScheduleXlsxLabelSets
): Promise<CsvParseResult> {
  return new ExcelJS.Workbook().xlsx.load(buffer).then((wb) => {
    const rows: CsvParsedRow[] = [];
    const errors: CsvRowError[] = [];
    const sheet = wb.getWorksheet('Schedule');
    if (!sheet) {
      return {
        rows,
        errors: [
          {
            rowNumber: 0,
            message:
              'Could not find a "Schedule" sheet in this file — please use the downloaded template',
          },
        ],
      };
    }

    const prayerLookup = buildLookup(withLabelSets(PRAYER_ALIAS_SEED, labelSets.prayers));
    const dayTypeLookup = buildLookup(withLabelSets(DAYTYPE_ALIAS_SEED, labelSets.dayTypes));
    const timeTypeLookup = buildLookup(withLabelSets(TIMETYPE_ALIAS_SEED, labelSets.timeTypes));
    const roundDirLookup = buildLookup(withLabelSets(ROUNDDIR_ALIAS_SEED, labelSets.roundDirections));

    const seenKeys = new Map<string, number>();

    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
      if (rowNumber === 1) return;

      // Skip blank example-deleted rows: require at least prayer or dayTypes
      const prayerRaw = cellText(row, 1);
      const dayTypesRaw = cellText(row, 2);
      if (!prayerRaw && !dayTypesRaw) return;

      const prayerType = prayerLookup[normalize(prayerRaw)] as PrayerType | undefined;
      if (!prayerType) {
        errors.push({
          rowNumber,
          message: `Unknown prayer "${prayerRaw}" — please pick from the dropdown`,
        });
        return;
      }

      const dayParsed = parseDayTypesCell(dayTypesRaw, dayTypeLookup);
      if (dayParsed.error || !dayParsed.dayTypes) {
        errors.push({ rowNumber, message: dayParsed.error || 'Invalid dayTypes' });
        return;
      }
      const dayTypes = dayParsed.dayTypes;

      const prayerErr = prayerDayTypesAllowed(prayerType, dayTypes);
      if (prayerErr) {
        errors.push({ rowNumber, message: prayerErr });
        return;
      }

      const timeTypeRaw = cellText(row, 3);
      const timeTypeKey = timeTypeLookup[normalize(timeTypeRaw)];
      let timeRule: TimeRule;

      if (timeTypeKey === 'fixed') {
        const fixedTime = cellText(row, 8);
        if (!TIME_RE.test(fixedTime)) {
          errors.push({
            rowNumber,
            message: `fixedTime "${fixedTime}" must be in 24-hour HH:mm format, e.g. 18:30`,
          });
          return;
        }
        timeRule = { type: 'fixed', time: fixedTime };
      } else if (timeTypeKey === 'zman') {
        const baseZmanRaw = cellText(row, 4);
        const baseZman = ZMAN_KEY_LOOKUP[normalize(baseZmanRaw)];
        if (!baseZman) {
          errors.push({
            rowNumber,
            message: `Unknown baseZman "${baseZmanRaw}" — please pick from the dropdown`,
          });
          return;
        }
        const offsetRaw = cellText(row, 5);
        const offsetMinutes = Number(offsetRaw);
        if (offsetRaw === '' || Number.isNaN(offsetMinutes)) {
          errors.push({
            rowNumber,
            message: `offsetMinutes "${offsetRaw}" must be a number, e.g. -20 or 15`,
          });
          return;
        }
        const roundToRaw = cellText(row, 6);
        const roundDirectionRaw = cellText(row, 7);
        let rounding: RoundingConfig | undefined;
        if (roundToRaw || roundDirectionRaw) {
          if (!roundToRaw || !roundDirectionRaw) {
            errors.push({
              rowNumber,
              message: 'roundTo and roundDirection must both be filled in, or both left blank',
            });
            return;
          }
          const roundTo = Number(roundToRaw);
          if (![5, 10, 15, 30].includes(roundTo)) {
            errors.push({
              rowNumber,
              message: `roundTo "${roundToRaw}" must be 5, 10, 15, or 30`,
            });
            return;
          }
          const direction = roundDirLookup[normalize(roundDirectionRaw)] as
            | 'up'
            | 'down'
            | 'nearest'
            | undefined;
          if (!direction) {
            errors.push({
              rowNumber,
              message: `roundDirection "${roundDirectionRaw}" must be up, down, or nearest`,
            });
            return;
          }
          rounding = { to: roundTo as 5 | 10 | 15 | 30, direction };
        }
        timeRule = {
          type: 'zmanOffset',
          baseZman,
          offsetMinutes,
          ...(rounding ? { rounding } : {}),
        };
      } else {
        errors.push({
          rowNumber,
          message: `timeType "${timeTypeRaw}" must be picked from the dropdown`,
        });
        return;
      }

      const note = cellText(row, 9);
      const noteHe = cellText(row, 10);

      const nightRaw = cellText(row, 11);
      const night = nightRaw ? NIGHT_LOOKUP[normalize(nightRaw)] : undefined;
      if (nightRaw && !night) {
        errors.push({ rowNumber, message: `night "${nightRaw}" must be start (the night that starts the day) or end (the night after it)` });
        return;
      }
      const fromRaw = cellText(row, 12);
      const selichotFrom = fromRaw ? SELICHOT_FROM_LOOKUP[normalize(fromRaw)] : undefined;
      if (fromRaw && !selichotFrom) {
        errors.push({ rowNumber, message: `selichotFrom "${fromRaw}" must be elul2 or firstNight` });
        return;
      }

      const data: Omit<ScheduleEntry, 'id' | 'overrides'> = {
        prayerType,
        dayTypes,
        timeRule,
        active: true,
        ...(note ? { note } : {}),
        ...(noteHe ? { noteHe } : {}),
        ...(night && (prayerType === 'maariv' || prayerType === 'hakafot' || prayerType === 'megillah') ? { night } : {}),
        ...(selichotFrom && prayerType === 'selichot' ? { selichotFrom } : {}),
      };

      const dupeKey = scheduleMatchKey(data);
      if (seenKeys.has(dupeKey)) {
        errors.push({
          rowNumber,
          message: `Duplicate row — same prayer, day types, and note as row ${seenKeys.get(dupeKey)}`,
        });
        return;
      }
      seenKeys.set(dupeKey, rowNumber);

      rows.push({ rowNumber, data });
    });

    return { rows, errors };
  });
}

/** Upsert parsed rows into the shul's flat schedules array. */
export function mergeCsvRowsIntoSchedules(
  schedules: ScheduleEntry[],
  rows: CsvParsedRow[]
): ScheduleEntry[] {
  const next = [...schedules];

  for (const row of rows) {
    const key = scheduleMatchKey(row.data);
    const idx = next.findIndex((s) => scheduleMatchKey(s) === key);

    if (idx >= 0) {
      const existing = next[idx];
      next[idx] = {
        id: existing.id,
        prayerType: row.data.prayerType,
        dayTypes: row.data.dayTypes,
        timeRule: row.data.timeRule,
        active: true,
        overrides: existing.overrides || [],
        ...(row.data.night ? { night: row.data.night } : {}),
        ...(row.data.selichotFrom ? { selichotFrom: row.data.selichotFrom } : {}),
        ...(row.data.note ? { note: row.data.note } : {}),
        ...(row.data.noteHe ? { noteHe: row.data.noteHe } : existing.noteHe ? { noteHe: existing.noteHe } : {}),
      };
    } else {
      next.push({
        id: crypto.randomUUID(),
        prayerType: row.data.prayerType,
        dayTypes: row.data.dayTypes,
        timeRule: row.data.timeRule,
        active: true,
        overrides: [],
        ...(row.data.night ? { night: row.data.night } : {}),
        ...(row.data.selichotFrom ? { selichotFrom: row.data.selichotFrom } : {}),
        ...(row.data.note ? { note: row.data.note } : {}),
        ...(row.data.noteHe ? { noteHe: row.data.noteHe } : {}),
      });
    }
  }

  return next;
}

export async function generateScheduleXlsxTemplate(
  labelSets: ScheduleXlsxLabelSets,
  isHebrew: boolean
): Promise<Blob> {
  const wb = new ExcelJS.Workbook();

  const instructions = wb.addWorksheet('Instructions');
  instructions.columns = [{ width: 60 }, { width: 60 }];
  instructions.addRow(['English', 'עברית']);
  instructions.getRow(1).font = { bold: true };
  instructions.addRow([labelSets.instructionsTitle.en, labelSets.instructionsTitle.he]);
  instructions.getRow(2).font = { bold: true };
  instructions.addRow([labelSets.instructionsBody.en, labelSets.instructionsBody.he]);
  instructions.getRow(3).alignment = { wrapText: true, vertical: 'top' };
  instructions.getRow(3).height = 120;

  // Reference list of valid day-type labels (no dropdown — multi-value cell)
  instructions.addRow([]);
  instructions.addRow([
    'Valid dayTypes (comma-separate in one cell):',
    'סוגי יום תקפים (מופרדים בפסיק בתא אחד):',
  ]);
  instructions.getRow(5).font = { bold: true };
  const dayTypeListEn = labelSets.dayTypes.map((d) => d.en).join(', ');
  const dayTypeListHe = labelSets.dayTypes.map((d) => d.he).join(', ');
  instructions.addRow([dayTypeListEn, dayTypeListHe]);
  instructions.getRow(6).alignment = { wrapText: true, vertical: 'top' };

  const zmanKeys = Object.keys(MYZMANIM_FIELDS);

  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';
  // A: prayer, B: timeType, C: baseZman, D: roundTo, E: roundDirection
  labelSets.prayers.forEach((p, i) => {
    lists.getCell(i + 1, 1).value = isHebrew ? p.he : p.en;
  });
  labelSets.timeTypes.forEach((t, i) => {
    lists.getCell(i + 1, 2).value = isHebrew ? t.he : t.en;
  });
  zmanKeys.forEach((key, i) => {
    lists.getCell(i + 1, 3).value = MYZMANIM_FIELDS[key];
  });
  lists.getCell(zmanKeys.length + 1, 3).value = 'הדלקת נרות';
  [5, 10, 15, 30].forEach((n, i) => {
    lists.getCell(i + 1, 4).value = n;
  });
  labelSets.roundDirections.forEach((d, i) => {
    lists.getCell(i + 1, 5).value = isHebrew ? d.he : d.en;
  });

  const sheet = wb.addWorksheet('Schedule');
  sheet.columns = [
    { header: 'prayer', key: 'prayer', width: 16 },
    { header: 'dayTypes', key: 'dayTypes', width: 28 },
    { header: 'timeType', key: 'timeType', width: 14 },
    { header: 'baseZman', key: 'baseZman', width: 16 },
    { header: 'offsetMinutes', key: 'offsetMinutes', width: 14 },
    { header: 'roundTo', key: 'roundTo', width: 10 },
    { header: 'roundDirection', key: 'roundDirection', width: 14 },
    { header: 'fixedTime', key: 'fixedTime', width: 12 },
    { header: 'note', key: 'note', width: 18 },
    { header: 'noteHe', key: 'noteHe', width: 18 },
    { header: 'night', key: 'night', width: 10 },
    { header: 'selichotFrom', key: 'selichotFrom', width: 14 },
  ];
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  const weekdayLabel = isHebrew ? labelSets.dayTypes[0].he : labelSets.dayTypes[0].en;
  const erevLabel = isHebrew ? labelSets.dayTypes[1].he : labelSets.dayTypes[1].en;
  const multiDayExample = `${weekdayLabel}, ${erevLabel}`;

  sheet.addRow({
    prayer: isHebrew ? labelSets.prayers[1].he : labelSets.prayers[1].en, // shacharit
    dayTypes: multiDayExample,
    timeType: isHebrew ? labelSets.timeTypes[0].he : labelSets.timeTypes[0].en,
    baseZman: '',
    offsetMinutes: '',
    roundTo: '',
    roundDirection: '',
    fixedTime: '07:00',
    note: 'Downstairs',
    noteHe: 'קומה תחתונה',
  });
  sheet.addRow({
    prayer: isHebrew ? labelSets.prayers[3].he : labelSets.prayers[3].en, // mincha
    dayTypes: weekdayLabel,
    timeType: isHebrew ? labelSets.timeTypes[1].he : labelSets.timeTypes[1].en,
    baseZman: MYZMANIM_FIELDS.SunsetDefault,
    offsetMinutes: -20,
    roundTo: 10,
    roundDirection: isHebrew ? labelSets.roundDirections[1].he : labelSets.roundDirections[1].en,
    fixedTime: '',
    note: '',
    noteHe: '',
  });

  const lastRow = 500;
  // Cols with dropdowns: prayer(1), timeType(3), baseZman(4), roundTo(6), roundDirection(7)
  // No dropdown on dayTypes(2) — free text, comma-separated
  const validations: [number, string][] = [
    [1, `Lists!$A$1:$A$${labelSets.prayers.length}`],
    [3, `Lists!$B$1:$B$${labelSets.timeTypes.length}`],
    [4, `Lists!$C$1:$C$${zmanKeys.length + 1}`],
    [6, `Lists!$D$1:$D$4`],
    [7, `Lists!$E$1:$E$${labelSets.roundDirections.length}`],
  ];
  for (let r = 2; r <= lastRow; r++) {
    validations.forEach(([col, ref]) => {
      sheet.getCell(r, col).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [ref],
        showErrorMessage: true,
        errorTitle: 'Invalid choice',
        error: 'Please choose a value from the dropdown list.',
      };
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
